import {
  DocumentDetailsContract,
  DocumentDetailsOthers,
} from "../../../api/services/client/models";
import { IdNameList } from "../../../api/services/common/models";
import {
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "../../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { StatusBadgeType } from "../../../components/statusBadge/StatusBadge";
import { DuplicateDataTypes } from "../../../helpers/constants";
import { LabelValue } from "../../../helpers/types/common";

export const CreateClientFormKey = {
  companyName: "companyName",
  englishCompanyName: "englishCompanyName",
  clientType: "clientType",
  industry: "industry",
  localClientRelationshipOwnerId: "localClientRelationshipOwnerId",
  addressCountry: "addressCountry",
  addressCountryName: "addressCountryName",
  billingCountry: "billingCountry",
  billingCountryName: "billingCountryName",
  taxVat: "taxVat",
  dedicatedProjectManager: "dedicatedProjectManager",
  originDate: "originDate",
  address1: "address1",
  address2: "address2",
  address3: "address3",
  country: "country",
  countryName: "countryName",
  city: "city",
  stateProvince: "stateProvince",
  postalCode: "postalCode",
  businessIdentificationNumber: "businessIdentificationNumber",
  siretNumber: "siretNumber",
  btwNummer: "btwNummer",
  nif: "nif",
  abnNumber: "abnNumber",
  nzbn: "nzbn",
  qualifiedInvoiceNumber: "qualifiedInvoiceNumber",
  cin: "cin",
  bin: "bin",
  tin: "tin",
  consolidatedInvoicing: "consolidatedInvoicing",
  activeCompany: "activeCompany",
  rebateClient: "rebateClient",
  earlyPayDiscount: "earlyPayDiscount",
  earlyPayDays: "earlyPayDays",
  earlyPayPercentage: "earlyPayPercentage",
  invoiceLanguage: "invoiceLanguage",
  notes: "notes",
  configurations: "configurations",
  billingConfigurationName: "billingConfigurationName",
  lineOfBusinessId: "lineOfBusinessId",
  contractDocumentId: "contractDocumentId",
  opportunityId: "opportunityId",
  opportunityName: "opportunityName",
  isOpportunityIdValidated: "isOpportunityIdValidated",
  paymentTermId: "paymentTermId",
  billingContactId: "billingContactId",
  hrContactId: "hrContactId",
  additionalInvoiceContactId: "additionalInvoiceContactId",
  outreachEligibilityId: "outreachEligibilityId",
  costCentre: "costCentre",
  businessUnit: "businessUnit",
  poNumberId: "poNumberId",
  candidateLocation: "candidateLocation",
  site: "site",
  projectManagerId: "projectManagerId",
  projectCoordinatorId: "projectCoordinatorId",
  additionalProjectResourceId: "additionalProjectResourceId",
  clientInformation1: "clientInformation1",
  clientInformation2: "clientInformation2",
  clientInformation3: "clientInformation3",
  hasEmployeeBand: "hasEmployeeBand",
  hasEmployeeId: "hasEmployeeId",
  specialBillingInstructions: "specialBillingInstructions",
  programTemplateConfigurations: "programTemplateConfigurations",
  addConfiguration: "addConfiguration",
  id: "id",
  deleteConfiguration: "deleteConfiguration",
  programTemplateId: "programTemplateId",
  alternateDisplayName: "alternateDisplayName",
  billingTypeId: "billingTypeId",
  billingTriggerId: "billingTriggerId",
  currencyId: "currencyId",
  fee: "fee",
  feeWithFormat: "feeWithFormat",
  header: "header",
  addProgramTemplate: "addProgramTemplate",
  deleteTemplate: "deleteTemplate",
  salesOfficeId: "salesOfficeId",
  salesOfficeName: "salesOfficeName",
  schedulePeriod: "schedulePeriod",
  feePaymentType: "feePaymentType",
  schedules: "schedules",
  milestones: "milestones",
  activities: "activities",
  feePercentage: "feePercentage",
  amount: "amount",
  amountWithFormat: "amountWithFormat",
  scheduleFrequency: "scheduleFrequency",
  addBillingConfiguration: "addBillingConfiguration",
  billingSchedules: "billingSchedules",
  description: "description",
} as const;

export type CreateClientSectionTypes =
  | "profile"
  | "clientMailingAddress"
  | "billingConfigurations";

export interface CreateClientMailingAddressProps {
  address1: string;
  address2?: string;
  address3?: string;
  country?: string;
  countryName?: string;
  city: string;
  stateProvince?: string;
  postalCode: string;
}
export interface CreateClientFormProps extends CreateClientMailingAddressProps {
  companyName: string;
  englishCompanyName?: string;
  clientType: string;
  industry: string;
  localClientRelationshipOwnerId?: string;
  addressCountry: string;
  billingCountry: string;
  taxVat?: string;
  dedicatedProjectManager?: string;
  originDate?: string;
  siretNumber?: string;
  btwNummer?: string;
  nif?: string;
  abnNumber?: string;
  nzbn?: string;
  qualifiedInvoiceNumber?: string;
  cin?: string;
  bin?: string;
  tin?: string;
  businessIdentificationNumber?: string;
  salesOffice: IdNameList;
}

export interface BillingDetailsProps {
  consolidatedInvoicing?: boolean;
  activeCompany?: boolean;
  rebateClient?: boolean;
  earlyPayDiscount?: boolean;
  earlyPayDays?: number;
  earlyPayPercentage?: number;
  notes?: string;
}

export const ClientContactResultFormKey = {
  contactName: "contactName",
  usedByClients: "usedByClients",
  usedByClientsName: "usedByClientsName",
  contactType: "contactType",
  isSurveyContact: "isSurveyContact",
  status: "status",
  contactNumber: "contactNumber",
  lastModifiedOn: "lastModifiedOn",
  tableBody: "tableBody",
  tableRow: "tableRow",
  tableFooter: "tableFooter",
  tablePagination: "tablePagination",
} as const;

export interface UsedByClient {
  clientId: string;
  clientName: string;
  isParent?: boolean;
  contactType: string[];
  addressCountry: string;
}

export interface ClientContact {
  clientContactId: string;
  firstName: string;
  lastName: string;
  usedByClients: UsedByClient[];
  contactType: string[];
  status: string;
  contactNumber: string;
  lastModifiedOn: string;
  isSurveyContact?: boolean;
  isAttachedToCurrentClient: boolean;
  edit: any;
  [key: string]: any;
}

export interface AddNewContactFormProps {
  prefix?: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  telephoneCountryCode?: string;
  telephoneISOCode?: string;
  telephoneNumber?: string;
  mobileCountryCode?: string;
  mobileISOCode?: string;
  mobileNumber?: string;
  workEmail?: string;
  surveyLanguage?: string;
  status?: string;
  contactType?: string[];
  isSurveyContact?: string;
}

export const AddContactFormKey = {
  prefix: "prefix",
  firstName: "firstName",
  lastName: "lastName",
  jobTitle: "jobTitle",
  telephoneCountryCode: "telephoneCountryCode",
  telephoneISOCode: "telephoneISOCode",
  telephoneNumber: "telephoneNumber",
  mobileCountryCode: "mobileCountryCode",
  mobileISOCode: "mobileISOCode",
  mobileNumber: "mobileNumber",
  workEmail: "workEmail",
  surveyLanguage: "surveyLanguage",
  status: "status",
  contactType: "contactType",
  isSurveyContact: "isSurveyContact",
} as const;

export const ViewContactFormKey = {
  name: "name",
  workEmail: "workEmail",
  jobTitle: "jobTitle",
  telephoneNumber: "telephoneNumber",
  mobileNumber: "mobileNumber",
  surveyLanguage: "surveyLanguage",
  status: "status",
  contactType: "contactType",
  selected: "selected",
  noUsedByClient: "noUsedByClient",
} as const;

export type BillingDetailsCheckboxKeys =
  | "consolidatedInvoicing"
  | "activeCompany"
  | "rebateClient"
  | "earlyPayDiscount";

export type BillingDetailsCheckboxState = Record<
  BillingDetailsCheckboxKeys,
  boolean
>;

export interface BillingConfigurationProgramTemplateItem {
  id?: string;
  programTemplateId: string;
  alternateDisplayName?: string;
  billingTypeId: string;
  billingTriggerId: string;
  currencyId: string;
  currency?: Currency; // for get API
  fee: string | number;
  billingSchedules?:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[];
  value?: string;
  feePaymentType?: string;
  specialBillingInstructions?: string;
  schedulePeriod?: string;
}

export interface BillingConfigurationItem {
  id?: string;
  billingConfigurationName: string;
  lineOfBusinessId: string;
  billingContactId?: string;
  hrContactId?: string;
  additionalInvoiceContactId?: string;
  outreachEligibilityId: string;
  costCentre?: string;
  businessUnit?: string;
  poNumberId?: string;
  poNumber?: string;
  candidateLocation?: string;
  site?: string;
  salesOffice?: IdNameList;
  projectManagerId?: string;
  projectCoordinatorId?: string;
  additionalProjectResourceId?: string;
  programTemplateConfigurations: BillingConfigurationProgramTemplateItem[];
  hasEmployeeBand: boolean;
  hasEmployeeId: boolean;

  clientInformation1?: string;
  clientInformation2?: string;
  clientInformation3?: string;
  paymentTermId?: string;
  paymentTerms?: IdNameList;
  opportunityId?: string;
  opportunityName?: string;
  contractDocument?: ContractDocument;
  isOpportunityIdValidated?: boolean;
}

export interface ContractDocument {
  id: string;
  contractId: string;
  name: string;
}

export const emptyBillingConfigurationItem: BillingConfigurationItem = {
  id: "",
  billingConfigurationName: "",
  lineOfBusinessId: "",
  billingContactId: "",
  hrContactId: "",
  outreachEligibilityId: "",
  costCentre: "",
  businessUnit: "",
  poNumberId: "",
  candidateLocation: "",
  site: "",
  projectManagerId: "",
  projectCoordinatorId: "",
  additionalProjectResourceId: "",
  programTemplateConfigurations: [],
  hasEmployeeBand: false,
  hasEmployeeId: false,
};

export const CreateClientDocumentsFormKey = {
  documentId: "documentId",
  documentType: "documentType",
  documentName: "documentName",
  geoScope: "geoScope",
  expirationDate: "expirationDate",
  exclusivityId: "exclusivityId",
  notes: "notes",
  status: "status",
  tableBody: "tableBody",
  tableRow: "tableRow",
  tableFooter: "tableFooter",
  tablePagination: "tablePagination",
  effectiveDate: "effectiveDate",
  usedByClient: "usedByClient",
  contractId: "contractId",
  documentOrigin: "documentOrigin",
  tablePaginationPageNo: "tablePaginationPageNo",
} as const;

export interface UniqueFiltersTypes {
  [CreateClientDocumentsFormKey.notes]: LabelValue[];
  [CreateClientDocumentsFormKey.exclusivityId]: LabelValue[];
}

export type DocumentType =
  | "client_contract"
  | "client_others"
  | "bulk_program_upload"
  | "program";

export type DocumentTypeGetAPI = "contract" | "others";

export interface ClientDocument {
  documentId: string;
  documentType: DocumentType;
  documentName: string;
  geoScope?: string;
  expirationDate?: string;
  effectiveDate?: string;
  exclusivityId?: string;
  isCancelled?: boolean;
  notes: string;
  status: string;
  contractId?: string;
  documentOrigin?: string;
  usedByClient?: string[];
  [key: string]: any;
}

export interface DocumentsProps {
  rows: ClientDocument[];
  onDocumentView?: (
    data: DocumentDetailsOthers | DocumentDetailsContract,
  ) => void;
  onDocumentEdit?: (documentId: string, documentType: string) => void;
  successHandler: () => void;
  isEditable?: boolean;
}

export const ResourcesFormKey = {
  resources: "resources",
  resourceName: "resourceName",
  resourceTypeId: "resourceTypeId",
  resourceId: "resourceId",
  languageId: "languageId",
  addResourceButton: "addResourceButton",
  header: "header",
  delete: "delete",
};

export interface Resource {
  clientResourceId?: string;
  resourceName: string;
  resourceId: string;
  languageId: string;
  resourceTypeId: string;
}

export const ProgramTemplateFormKey = {
  groupName: "groupName",
  programTemplates: "programTemplates",
  allCountries: "allCountries",
  programTemplateName: "programTemplateName",
  programTemplateId: "programTemplateId",
  solutionOfferingsName: "solutionOfferingsName",
  programClassName: "programClassName",
  countries: "countries",
  showCountry: "showCountry",
  header: "header",
  delete: "delete",
  addTemplateButton: "addTemplateButton",
  resourceGroupProgramTemplateMapId: "resourceGroupProgramTemplateMapId",
  programTemplateDropdownChangeStatus: "programTemplateDropdownChangeStatus",
};

export interface Country {
  countryName: string;
  countryId: string;
}

export interface ProgramTemplate {
  programTemplateName: string;
  programTemplateId: string;
  solutionOfferingsName: string;
  programClassName: string;
  countries: Country[];
  countriesAsString?: string;
  resourceGroupProgramTemplateMapId?: string;
}

export interface ClientSpecificContent {
  clientProfileId?: string;
  clientResourceGroupId: string;
  clientResourceGroupName: string;
  resources: Resource[];
  programTemplates: ProgramTemplate[];
  [key: string]: any;
}

export const ClientSpecificContentFormKey = {
  clientResourceGroupName: "clientResourceGroupName",
  resourceName: "resourceName",
  resourceTypeId: "resourceTypeId",
  resourceId: "resourceId",
  languageId: "languageId",
  programTemplateName: "programTemplateName",
  solutionOfferingsName: "solutionOfferingsName",
  programClassName: "programClassName",
  country: "country",
  countriesAsString: "countriesAsString",
  tableBody: "tableBody",
  tableRow: "tableRow",
  tableFooter: "tableFooter",
  tablePagination: "tablePagination",
} as const;

export const UploadDocumentFormKey = {
  documentType: "documentType",
  opportunityId: "opportunityId",
  opportunityName: "opportunityName",
  isOpportunityIdValidated: "isOpportunityIdValidated",
  contractId: "contractId",
  ownershipCountryId: "ownershipCountryId",
  exclusivityId: "exclusivityId",
  sowAutoRenewalClauseId: "sowAutoRenewalClauseId",
  sowStatusId: "sowStatusId",
  classificationId: "classificationId",
  serviceLevelAgreementsId: "serviceLevelAgreementsId",
  customDeliverablesId: "customDeliverablesId",
  paymentTermId: "paymentTermId",
  contractDuration: "contractDuration",
  engagementTypeId: "engagementTypeId",
  contractGeoScopeId: "contractGeoScopeId",
  documentName: "documentName",
  documentId: "documentId",
  contractGeoScopeCountries: "contractGeoScopeCountries",
  contractGeoScopeSingleCountry: "contractGeoScopeSingleCountry",
  sowAutoRenewalTerms: "sowAutoRenewalTerms",
  sowExpirationDate: "sowExpirationDate",
  sowContractEffectiveDate: "sowContractEffectiveDate",
  uploadDate: "uploadDate",
  effectiveDate: "effectiveDate",
  notes: "notes",
  statusId: "statusId",
  file: "file",
  sowExpiryDateWithoutFormatted: "sowExpiryDateWithoutFormatted",
  sowEffectiveDateWithoutFormatted: "sowEffectiveDateWithoutFormatted",
  contractProductLines: "contractProductLines",
} as const;

export const CancelContractDocumentFormKey = {
  cancellationReason: "cancellationReason",
  comment: "comment",
} as const;

export const ArchiveContractDocumentFormKey = {
  archivalReason: "archivalReason",
  archivalComments: "archivalComments",
} as const;

export const AddPurchaseOrderFormKey = {
  poNumber: "poNumber",
  effectiveDate: "effectiveDate",
  effectiveDateWithoutFormatted: "effectiveDateWithoutFormatted",
  statusId: "statusId",
  currencyTypeId: "currencyTypeId",
  initialAmount: "initialAmount",
  initialAmountFormatted: "initialAmountFormatted",
  notes: "notes",
  expiryDate: "expiryDate",
  expiryDateWithoutFormatted: "expiryDateWithoutFormatted",
} as const;

export const ReopenPurchaseOrderFormKey = {
  reOpenReason: "reOpenReason",
  expiryDate: "expiryDate",
} as const;

export const TransactionsFormKey = {
  transactionDate: "transactionDate",
  transactionType: "transactionType",
  startBalanceAmount: "startBalanceAmount",
  amount: "amount",
  totalBalanceAmount: "totalBalanceAmount",
  invoiceNumber: "invoiceNumber",
  projectOrProgramKey: "projectOrProgramKey",
} as const;

export const POManageTransactionsFormKey = {
  transactionAmount: "transactionAmount",
  transactionType: "transactionType",
} as const;

export interface UniquePOTransactionsResultFiltersTypes {
  transactionType: LabelValue[];
}

export interface PurchaseOrder {
  clientId: string;
  createdTimeStamp: string;
  currencyTypeId: string;
  currentAmount: number | string;
  effectiveDate: string;
  expiryDate: string;
  notes: string;
  poNumber: string;
  purchaseOrderId: string;
  statusId: StatusBadgeType;
  status: string;
  isInvoiceGenerated?: boolean;
  [key: string]: any;
}

export type PurchaseOrderActionType =
  | "edit"
  | "delete"
  | "viewPoTransactions"
  | "managePoTransactions"
  | "reOpenPoNumber";

export interface Currency {
  id: string;
  name: string;
}

export interface PurchaseOrderExpensePayload {
  programExpenses: {
    expenseId: string;
    lineNumber: number;
  }[];
  purchaseOrderId: string;
  feeOrExpenseAmount: number;
  totalTaxAmount: number;
  totalAmount: number;
  invoiceDate: string;
  paymentTermId: string;
}

export interface BillingConfigDuplicateData {
  type: DuplicateDataTypes;
  parentIndex: number | null;
  duplicateIndexes: number[];
}

export interface ProgramTemplateDuplicateDataDetails {
  parentIndex: number;
  programTemplateIndex: number;
  programTemplateId: string;
}
export interface ProgramTemplateDuplicateData {
  type: DuplicateDataTypes;
  duplicateDataDetails: ProgramTemplateDuplicateDataDetails[];
}

export const STATUS_YES = "Status.Yes";

export const InheritDocumentsSearchFormKey = {
  documentName: "documentName",
  contractId: "contractId",
  documentType: "documentType",
};

export interface InheritDocumentsFormType {
  documentName: string;
  contractId: string;
  documentType: string;
}

export const InheritDocumentsResultFormKey = {
  isChecked: "isChecked",
  clientName: "clientName",
  clientCode: "clientCode",
  documentName: "documentName",
  contractId: "contractId",
  documentType: "documentType",
  documentStatus: "statusId",
  effectiveDate: "effectiveDate",
  expirationDate: "expirationDate",
};
